import React, { useState, useEffect, useRef } from 'react';
import { Skeleton, Tooltip, Rate, Modal, Menu, notification, Avatar, Flex, Dropdown, Image, Tag, Select, Space, Popconfirm, Button, Card, Typography, Checkbox, Upload, theme, Input, ConfigProvider } from 'antd';
import { MinusCircleOutlined , MehOutlined, WarningOutlined, ExclamationCircleOutlined, AlertOutlined, FireOutlined, BugOutlined, DeleteOutlined, EditOutlined, PlusOutlined, CheckOutlined, LinkOutlined } from '@ant-design/icons';
import TgAvatar from './TgAvatar';
import NameAvatar from './NameAvatar';
import LinkAlert from './LinkAlert';
import TaskTag from './TaskTag';
import Editor from './Editor';
import TaskComments from './TaskComments';
import { taskPriorityIcons, taskNumberPriorityIcons } from './taskPriority';

import type { RootState } from '../store';
import type { SelectProps } from 'antd';
import type { Task } from '../../types';

import { MAX_TASK_IMAGES, MAX_TASK_VIDEOS, TASK_STATUS } from '../constants';
import { getTaskTitle } from '../utils/text';
import {
    decorateContentLinks,
    decoratePlainTextLinks,
    extractTaskUrls,
    mergeTaskLinks,
} from '../utils/links';
import {
    MEDIA_ACCEPT,
    splitTaskMedia,
    validateTaskMediaFile,
} from '../utils/media';
import { useSelector, useDispatch } from 'react-redux';
import useBoolean from '../hooks/useBoolean';
import useImagePaste from '../hooks/useImagePaste';

const { Text, Paragraph } = Typography;

interface TaskModalProps {
    isOpen: boolean;
    task: Task;
    onAddLink: () => void;
    onDeleteLink: (linkId: number) => void;
    onAddImage: (file: File) => void | Promise<{ imageId: number; imagePath: string } | void>;
    onDeleteImage: (imageId: number) => void;
    onSave: (payload: { taskName: string; taskContent: string }) => void;
    onAddTag: () => void;
    onDeleteTag: (index: number) => void;
    onDelete: () => void;
    onUpdateStatus: (status: number) => void;
    onShowActivities: () => void;
    onClose: () => void;
    onUpdatePriority: (priority: number) => void;
    onCopyLink?: () => void | Promise<boolean>;
}

// TODO: add select
const taskStatusOptions: SelectProps['options'] = [
    { value: TASK_STATUS.open, label: 'Открыт' },
    { value: TASK_STATUS.inWork, label: 'В работе' },
    { value: TASK_STATUS.fixed, label: 'Исправлен' },
    { value: TASK_STATUS.check, label: 'Проверка' },
    { value: TASK_STATUS.closed, label: 'Закрыт' },
    { value: TASK_STATUS.rejected, label: 'Отклонён' },
];

const TaskModal: React.FC<TaskModalProps> = (props) => {
    const {
        isOpen,
        task,
        onAddLink,
        onDeleteLink,
        onAddImage,
        onDeleteImage,
        onSave,
        onDeleteTag,
        onAddTag,
        onDelete,
        onUpdateStatus,
        onShowActivities,
        onUpdatePriority,
        onClose,
        onCopyLink,
    } = props;

    const [ api, contextHolder ] = notification.useNotification();
    const [ isEdit, setIsEdit ] = useState(false);
    const { images: photoFiles, videos: videoFiles } = splitTaskMedia(task.images);
    const imageCount = photoFiles.length;
    const videoCount = videoFiles.length;
    const canAddImage = imageCount < MAX_TASK_IMAGES;
    const canAddVideo = videoCount < MAX_TASK_VIDEOS;
    const canAddFile = canAddImage || canAddVideo;

    const notifyMediaError = (description: string) => {
        api.error({
            message: 'Ошибка',
            description,
        });
    };
    const [ draftTitle, setDraftTitle ] = useState(getTaskTitle(task));
    const [ contentPreview, setContentPreview ] = useState<{ visible: boolean; src: string }>({
        visible: false,
        src: '',
    });
    const [ linkTooltip, setLinkTooltip ] = useState<{ open?: boolean; title: string }>({
        title: 'Скопировать ссылку на карточку',
    });
    const linkTooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const dispatch = useDispatch();
    const settings = useSelector((state: RootState) => state.settings);
    const { token } = theme.useToken();
    const { colorText, colorTextSecondary, colorBorderSecondary, colorBgContainer, colorPrimary } = token;

    const resolveImageSrc = (imagePath: string) => {
        if (!imagePath) {
            return '';
        }
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }

        let normalized = imagePath.replace(/\\/g, '/');
        if (normalized.startsWith('./')) {
            normalized = normalized.slice(1);
        }
        if (!normalized.startsWith('/')) {
            normalized = `/${normalized}`;
        }

        // В БД: ./public/... → /public/...; раздаём как /api/public/...
        if (normalized.startsWith('/api/public/')) {
            return encodeURI(normalized);
        }
        if (normalized.startsWith('/public/')) {
            return encodeURI(`/api${normalized}`);
        }
        return encodeURI(normalized);
    };

    useEffect(() => {
        if (isOpen) {
            setIsEdit(false);
            setDraftTitle(getTaskTitle(task));
        }
    }, [isOpen, task.taskId, task.taskName, task.taskContent]);

    useImagePaste({
        onAddImage: (file) => {
            if (isOpen && !isEdit) {
                const error = validateTaskMediaFile(file, { imageCount, videoCount });
                if (error) {
                    notifyMediaError(error);
                    return;
                }
                onAddImage(file);
            }
        },
        onError: (message: string) => api.error({ message }),
    });

    const handleUploadImageForEditor = async (file: File): Promise<string | null> => {
        const error = validateTaskMediaFile(file, { imageCount, videoCount });
        if (error) {
            notifyMediaError(error);
            return null;
        }

        try {
            const result = await Promise.resolve(onAddImage(file));
            const path = result?.imagePath;
            if (!path) {
                return null;
            }
            if (path.startsWith('/public/')) {
                return `/api${path}`;
            }
            return path;
        } catch {
            return null;
        }
    };

    const handleUpload = (file: File) => {
        const error = validateTaskMediaFile(file, { imageCount, videoCount });
        if (error) {
            notifyMediaError(error);
            return false;
        }

        onAddImage(file);
        return false;
    };

    const handleSaveEdit = (newContent: string) => {
        const nextTitle = draftTitle.trim() || getTaskTitle(task);
        onSave({
            taskName: nextTitle,
            taskContent: newContent,
        });
        setIsEdit(false);
    };

    const handleContentClick = (event: React.MouseEvent<HTMLElement>) => {
        const rawTarget = event.target as HTMLElement | null;
        const anchor = rawTarget?.closest?.('a');
        if (anchor instanceof HTMLAnchorElement) {
            // Автофокус/синтетический click при переоткрытии модалки не должен
            // уводить в новую вкладку (ссылки в описании с target=_blank).
            if (!event.isTrusted) {
                event.preventDefault();
            }
            return;
        }

        const target = event.target as HTMLElement | null;
        if (target?.closest?.('video')) {
            return;
        }
        if (!target || target.tagName !== 'IMG' || target.classList.contains('task-content-link-favicon')) {
            return;
        }

        const img = target as HTMLImageElement;
        const src = img.currentSrc || img.src;
        if (!src) {
            return;
        }

        event.preventDefault();
        setContentPreview({ visible: true, src });
    };

    const handleCancelEdit = () => {
        setDraftTitle(getTaskTitle(task));
        setIsEdit(false);
    };

    useEffect(() => {
        return () => {
            if (linkTooltipTimerRef.current) {
                clearTimeout(linkTooltipTimerRef.current);
            }
        };
    }, []);

    const handleCopyLinkClick = async () => {
        if (!onCopyLink) {
            return;
        }
        const result = await Promise.resolve(onCopyLink());
        if (result === false) {
            return;
        }
        if (linkTooltipTimerRef.current) {
            clearTimeout(linkTooltipTimerRef.current);
        }
        setLinkTooltip({ open: true, title: 'Ссылка скопирована' });
        linkTooltipTimerRef.current = setTimeout(() => {
            setLinkTooltip({ title: 'Скопировать ссылку на карточку' });
            linkTooltipTimerRef.current = null;
        }, 1600);
    };

    const displayLinks = mergeTaskLinks(task.links, extractTaskUrls(task));

    const copyLinkButton = onCopyLink ? (
        <Tooltip
            title={linkTooltip.title}
            open={linkTooltip.open}
            onOpenChange={(open) => {
                if (linkTooltip.open && linkTooltip.title === 'Ссылка скопирована') {
                    return;
                }
                if (!open) {
                    setLinkTooltip((prev) => ({ ...prev, open: undefined }));
                }
            }}
        >
            <Button
                type="text"
                size="small"
                icon={<LinkOutlined />}
                onClick={handleCopyLinkClick}
                style={{ color: colorText, paddingInline: 6 }}
            >
                Ссылка
            </Button>
        </Tooltip>
    ) : null;

    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={1000}
            destroyOnHidden
            focusTriggerAfterClose={false}
            styles={{ body: { paddingTop: 12 } }}
        >
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                {task.createDate && (
                    <Text style={{ color: colorText }}>
                        {new Date(task.createDate).toLocaleString([], {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        })}
                    </Text>
                )}
                {copyLinkButton}
            </div>

            <Flex className="s-detail">
                <div
                    className="s-detail__text"
                    style={{
                        background: colorBgContainer,
                        borderColor: colorBorderSecondary,
                        color: colorText,
                    }}
                >
                    {isEdit ? (
                        <>
                            <Input
                                className="s-detail__title-input"
                                value={draftTitle}
                                onChange={(e) => setDraftTitle(e.target.value)}
                                placeholder="Название задачи"
                                maxLength={500}
                                style={{ fontSize: 20, fontWeight: 650 }}
                            />
                            <Editor
                                initialContent={task.taskContent || ''}
                                onSave={handleSaveEdit}
                                onCancel={handleCancelEdit}
                                onUploadImage={handleUploadImageForEditor}
                                disableImageUpload={!canAddImage}
                                disableVideoUpload={!canAddVideo}
                            />
                        </>
                    ) : (
                        <div className="s-detail__content" style={{ color: colorText }}>
                            <h2
                                className="s-detail__title"
                                style={{ color: colorText }}
                                onClick={handleContentClick}
                                dangerouslySetInnerHTML={{ __html: decoratePlainTextLinks(getTaskTitle(task)) }}
                            />
                            {task.taskContent ? (
                                <div
                                    className="s-detail__content-body"
                                    onClick={handleContentClick}
                                    dangerouslySetInnerHTML={{ __html: decorateContentLinks(task.taskContent) }}
                                />
                            ) : (
                                <Text type="secondary">Описание пока не добавлено</Text>
                            )}
                            <Button
                                style={{ position: 'absolute', top: 5, right: 5, color: colorText }}
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => setIsEdit(true)}
                            />
                            <Image
                                style={{ display: 'none' }}
                                src={contentPreview.src}
                                preview={{
                                    visible: contentPreview.visible,
                                    src: contentPreview.src,
                                    onVisibleChange: (visible) => {
                                        setContentPreview((prev) => ({
                                            ...prev,
                                            visible,
                                            src: visible ? prev.src : '',
                                        }));
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>

                <div
                    className="s-detail__params"
                    style={{ borderLeftColor: colorBorderSecondary, color: colorText }}
                >
                    <Flex className="s-detail__info">
                        <div>
                            <Text style={{ color: colorText }}>Автор</Text>
                        </div>
                        <div>
                            {task.user ?
                                <TgAvatar user={task.user} />
                                : 
                                <NameAvatar whoAddTask={task.whoAddTask} />
                            }
                        </div>
                    </Flex>

                    <Flex className="s-detail__info">
                        <div>
                            <Text style={{ color: colorText }}>Статус</Text>
                        </div>
                        <Space align="center" size={2}>
                            <Text style={{ color: colorText }}>{TASK_STATUS[task.status]}</Text>
                        </Space>
                    </Flex>

                    <Flex className="s-detail__info">
                        <div>
                            <Text style={{ color: colorText }}>Теги</Text>
                        </div>
                        <Space align="center" wrap size={2}>
                            {task.tags.map((tag, index) => (
                                <TaskTag
                                    key={index}
                                    tag={tag}
                                    closable
                                    onClose={(e) => {
                                        e.preventDefault();
                                        onDeleteTag(index)
                                    }}
                                />
                            ))}
                            <Button
                                type="text"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={onAddTag}
                                style={{ color: colorText }}
                            />
                        </Space>
                    </Flex>

                    <Flex className="s-detail__info">
                        <div>
                            <Text style={{ color: colorText }}>Приоритет</Text>
                        </div>
                        <Space align="center" wrap size={2}>
                            <ConfigProvider
                                theme={{
                                    components: {
                                        Rate: {
                                            // Дефолтный colorFillContent на светлой теме почти невидимый
                                            starBg: settings.theme?.isDark
                                                ? 'rgba(255, 255, 255, 0.35)'
                                                : colorTextSecondary,
                                        },
                                    },
                                }}
                            >
                                <Rate
                                    className="task-priority-rate"
                                    value={task.priority || undefined}
                                    count={5}
                                    character={({ index = 0 }) => settings.priorityNumberIcons ? taskNumberPriorityIcons[index + 1] : taskPriorityIcons[index + 1]}
                                    onChange={onUpdatePriority}
                                />
                            </ConfigProvider>
                        </Space>
                    </Flex>

                    <Flex className="s-detail__info">
                        <div>
                            <Text style={{ color: colorText }}>Ссылки</Text>
                        </div>
                        <Space align="center" wrap size={8}>
                            {displayLinks.map((link) => (
                                <LinkAlert
                                    key={link.key}
                                    url={link.url}
                                    onDelete={
                                        link.linkId && !link.fromContent
                                            ? () => onDeleteLink(link.linkId as number)
                                            : undefined
                                    }
                                />
                            ))}

                            <Button
                                icon={<PlusOutlined />}
                                onClick={onAddLink}
                                size="small"
                                type="text"
                                style={{ color: colorText }}
                            />
                        </Space>
                    </Flex>

                    <div style={{ marginTop: 8 }}>
                        <Text style={{ display: 'block', marginBottom: 8, color: colorText }}>
                            Файлы
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                                {imageCount}/{MAX_TASK_IMAGES} фото · {videoCount}/{MAX_TASK_VIDEOS} видео
                            </Text>
                        </Text>
                        <Flex vertical gap={8}>
                            {videoFiles.map((file) => (
                                <div key={file.imageId} style={{ position: 'relative' }}>
                                    <video
                                        className="task-file-video"
                                        src={resolveImageSrc(file.imagePath)}
                                        controls
                                        preload="metadata"
                                        playsInline
                                        style={{
                                            width: '100%',
                                            maxHeight: 220,
                                            borderRadius: 8,
                                            background: '#000',
                                            border: `1px solid ${colorBorderSecondary}`,
                                        }}
                                    />
                                    <Popconfirm
                                        title="Удалить это видео?"
                                        onConfirm={() => onDeleteImage(file.imageId)}
                                    >
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            style={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                            }}
                                        />
                                    </Popconfirm>
                                </div>
                            ))}
                            <Image.PreviewGroup>
                                <Flex wrap="wrap" gap={8}>
                                    {photoFiles.map((image) => (
                                        <div key={image.imageId} style={{ position: 'relative' }}>
                                            <Image
                                                width={100}
                                                height={100}
                                                src={resolveImageSrc(image.imagePath)}
                                                style={{
                                                    objectFit: 'cover',
                                                    borderRadius: 8,
                                                    border: `1px solid ${colorBorderSecondary}`,
                                                }}
                                            />
                                            <Popconfirm
                                                title="Вы уверены, что хотите удалить это изображение?"
                                                onConfirm={() => onDeleteImage(image.imageId)}
                                            >
                                                <Button
                                                    icon={<DeleteOutlined />}
                                                    size="small"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                    }}
                                                />
                                            </Popconfirm>
                                        </div>
                                    ))}

                                    {canAddFile && (
                                        <Upload
                                            className="task-upload-tile"
                                            listType="picture-card"
                                            showUploadList={false}
                                            beforeUpload={handleUpload}
                                            accept={MEDIA_ACCEPT}
                                            maxCount={1}
                                            style={{ ['--task-upload-border' as string]: colorPrimary } as React.CSSProperties}
                                        >
                                            <PlusOutlined />
                                        </Upload>
                                    )}
                                </Flex>
                            </Image.PreviewGroup>
                        </Flex>
                    </div>
                </div>
            </Flex>

            <div
                className="task-modal-comments"
                style={{ borderTopColor: colorBorderSecondary }}
            >
                <div className="task-modal-comments__label" style={{ color: colorText }}>
                    Комментарии
                </div>
                <TaskComments task={task} />
            </div>

            {contextHolder}
        </Modal>
    );
};

export default TaskModal;
